import React, { useEffect, useState } from 'react';
import ReForm, { type NestedObject, type JsonFormsData } from '@restore/reform';
import { Box, Container, styled } from '@mui/system';
import { schema } from './schema';
import { uischema } from './uischema';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { paths, permissions } from 'src/paths';
import { datadogRum } from '@datadog/browser-rum';
import { getRealmName } from 'src/routes/AuthContext';
import SucessModal from '../sucess-modal';
import CommonLoader from 'src/utils/common-loader';
import DocumentService from 'src/services/documentService';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  SvgIcon,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import { type AddendumNotePayload } from 'src/services/documentService/submitAddendumNote';
import { codesApiUrl, codesApiSecurityCode } from 'src/config';

const { patients, patientDetails, episodeOfCare, details, addNewEpisodeOfCare } = paths;

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

const DetailsContainer = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  gap: '1em',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const PatientInfoContainer = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  gap: '1em',
  flexGrop: 1,
}));

type AddendumNoteProps = {
  formId?: string;
  webhookUuid?: string;
  patientData?: any;
  loadPrefill?: boolean;
};

const MedicalCodeEditor: React.FC<AddendumNoteProps> = ({
  formId,
  webhookUuid,
  patientData,
  loadPrefill = false,
}) => {
  const [formStatus, setFormStatus] = useState<FormStatus>('loading');
  const [prefill, setPrefill] = useState<NestedObject>({});
  const [errorMessage, setErrorMessage] = useState<string>('Something went wrong');
  const navigate = useNavigate();
  const realm = getRealmName();
  const location = useLocation();

  const therapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const { EDIT_MEDICAL_CODE } = permissions;

  const facilityId = localStorage.getItem('facilityId');
  const getAccessToken = async () => ''; // Don't need this, so making it nothing

  useEffect(() => {
    if (formStatus !== 'loading' || !therapistProfile) return;

    const therapistDefaults: NestedObject = {
      licenseNumber: therapistProfile.licenseNumber || '',
      therapistFirstName: therapistProfile.firstName || '',
      therapistLastName: therapistProfile.lastName || '',
    };

    // Create flow: no existing submission to load — prefill with the therapist's own details.
    if (!loadPrefill) {
      setPrefill(therapistDefaults);
      setFormStatus('ready');
      return;
    }

    // Edit flow: fetch any previously-entered data via the same endpoint the other reform
    // documents use (getSchema → response.data). Wait until the identifiers are available.
    if (!realm || !formId || !webhookUuid) return;

    DocumentService.getSchema(realm, formId, webhookUuid)
      .then((response) => {
        const data: any = response?.data;
        if (!response?.error && data) {
          // The saved data is nested ({ generalInformation, additionalComments }) — the same
          // shape we submit — while the local schema is flat, so map it back.
          const gi: any = data.generalInformation ?? {};
          const mapped: NestedObject = {
            licenseNumber: gi.licenseNumber ?? data.licenseNumber ?? therapistDefaults.licenseNumber,
            therapistFirstName:
              gi.therapistFirstName ?? data.therapistFirstName ?? therapistDefaults.therapistFirstName,
            therapistLastName:
              gi.therapistLastName ?? data.therapistLastName ?? therapistDefaults.therapistLastName,
            notes: data.additionalComments ?? data.notes ?? '',
          };
          const dateOfService = data.dateOfService ?? gi.dateOfService;
          if (dateOfService) {
            mapped.dateOfService = dateOfService;
          }
          setPrefill(mapped);
        } else {
          setPrefill(therapistDefaults);
        }
        setFormStatus('ready');
      })
      .catch((error) => {
        console.error('Error loading addendum note prefill:', error);
        setPrefill(therapistDefaults);
        setFormStatus('ready');
      });
  }, [formStatus, therapistProfile, loadPrefill, realm, formId, webhookUuid]);

  const submitForm = async (data: JsonFormsData) => {
    setFormStatus('submitting');
    const submission: AddendumNotePayload = {
      generalInformation: {
        patientFirstName: patientData?.firstName || '',
        patientLastName: patientData?.lastName || '',
        therapistFirstName: data.therapistFirstName || therapistProfile.firstName || '',
        therapistLastName: data.therapistLastName || therapistProfile.lastName || '',
        licenseNumber: data.licenseNumber || therapistProfile.licenseNumber || '',
        credentials: therapistProfile.credentials || '',
      },
      additionalComments: data.notes || '',
    };

    if (realm && therapistProfile && formId && webhookUuid && facilityId) {
      try {
        const response = await DocumentService.submitAddendumNote(
          realm,
          formId,
          webhookUuid,
          facilityId,
          submission
        );

        if (response && response.error === false) {
          datadogRum.addAction('addendumNote_submitForm_SUCCESS', { realm });
          setFormStatus('success');
        } else {
          datadogRum.addAction('addendumNote_submitForm_FAILED', { realm, error: response.error });
          setErrorMessage(
            typeof response.error === 'string' ? response.error : 'Failed to submit addendum note'
          );
          setFormStatus('error');
        }
      } catch (error) {
        console.error('Error submitting addendum note:', error);
        datadogRum.addAction('addendumNote_submitForm_ERROR', { realm, error: error.message });
        setErrorMessage('An error occurred while submitting the form');
        setFormStatus('error');
      }
    } else {
      // Missing one of the required identifiers (formId/webhookUuid/facility) — surface it
      // instead of silently doing nothing.
      datadogRum.addAction('addendumNote_submitForm_MISSING_DATA', {
        realm,
        hasFormId: Boolean(formId),
        hasWebhookUuid: Boolean(webhookUuid),
        hasFacilityId: Boolean(facilityId),
      });
      setErrorMessage('Missing required submission data. Please reload the page and try again.');
      setFormStatus('error');
    }
  };

  const submitCallback = (data: JsonFormsData) => {
    // Addendum notes submit directly — the ICD-10 "Submit and Sign" confirmation dialog
    // was inherited from MedicalCodeEditor and does not apply here.
    submitForm(data);
  };

  const navigateToEpisodeOfCare = () => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareTitle: location.state.episodeOfCareTitle,
        episodeOfCareId: location.state.episodeOfCareId,
        getPatientId: location.state.patientData?.uuid ?? location.state.patient?.uuid,
      },
    });
  };

  const handleSuccess = () => {
    navigateToEpisodeOfCare();
  };

  const handleError = () => {
    navigateToEpisodeOfCare();
  };

  let component;

  switch (formStatus) {
    case 'loading':
      component = <CommonLoader />;
      break;

    case 'ready':
      component = (
        <ReForm
          schema={schema}
          uiSchema={uischema}
          submitCallback={submitCallback}
          initialData={prefill}
          formUuid=""
          realm=""
          apiUrl=""
          aiApiUrl=""
          codesApiUrl={codesApiUrl}
          codesApiSecurityCode={codesApiSecurityCode}
          getAccessToken={getAccessToken}
          facilityId={facilityId || ''}
          discipline={therapistProfile.discipline}
        />
      );
      break;

    case 'submitting':
      component = <CommonLoader />;
      break;

    case 'success':
      component = (
        <SucessModal
          title="Submitted"
          message="Your submission has been received"
          close={handleSuccess}
        />
      );
      break;

    default:
      component = (
        <SucessModal
          title="Error"
          message={errorMessage}
          isSucess={false}
          close={handleError}
        />
      );

      break;
  }

  return (
    <>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // py: 8,
        }}
      >
        {/* Align the form's left edge with the page heading: the heading sits at 4rem from
            the page edge while the parent content Box already adds 24px (p={3}), so the
            container adds the remaining 2.5rem. maxWidth/gutters are dropped to avoid the
            max-width centering that pushes the form right of the heading on wide screens. */}
        <Container
          maxWidth={false}
          disableGutters
          sx={{ px: '2.5rem' }}
        >
          {component}
        </Container>
      </Box>
    </>
  );
};

export default MedicalCodeEditor;
